import React from 'react';
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
  Font
} from '@react-pdf/renderer';

Font.register({
  family: 'Helvetica-Bold',
  src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-bold-webfont.ttf'
});

const styles = StyleSheet.create({
  page: {
    padding: 20,
    fontSize: 8,
    fontFamily: 'Helvetica',
    lineHeight: 1.4
  },

  /* ================= HEADER ================= */
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8
  },
  logo: { width: 70, height: 45 },
  companyRight: { width: '70%', textAlign: 'right' },
  companyName: { fontSize: 14, fontWeight: 'bold' },

  /* ================= ORANGE BAR ================= */
  orangeBar: {
    flexDirection: 'row',
    backgroundColor: '#f58220',
    paddingVertical: 6,
    marginBottom: 6
  },
  barText: {
    flex: 1,
    color: '#fff',
    textAlign: 'center',
    fontWeight: 'bold'
  },

  /* ================= INFO GRID ================= */
  row: {
    flexDirection: 'row',
    borderBottomWidth: 0.7,
    borderColor: '#ccc',
    paddingVertical: 4
  },
  label: { width: '18%', fontWeight: 'bold' },
  value: { width: '32%' },

  /* ================= FROM / TO BOX ================= */
  box: {
    borderWidth: 0.8,
    borderColor: '#000',
    marginTop: 6
  },
  boxHeader: {
    flexDirection: 'row',
    backgroundColor: '#f2f2f2',
    borderBottomWidth: 0.8,
    borderColor: '#000',
    padding: 4,
    fontWeight: 'bold'
  },
  boxCol: { flex: 1 },
  boxAddress: {
    padding: 5,
    borderTopWidth: 0.5,
    borderColor: '#ccc'
  },

  /* ================= TABLE ================= */
  table: {
    borderWidth: 0.8,
    borderColor: '#000',
    marginTop: 8
  },
  tableTitle: {
    backgroundColor: '#f58220',
    color: '#fff',
    padding: 5,
    textAlign: 'center',
    fontWeight: 'bold',
    borderBottomWidth: 0.8,
    borderColor: '#000'
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 0.6,
    borderColor: '#000'
  },
  tableLeft: {
    width: '75%',
    padding: 5,
    fontWeight: 'bold',
    borderRightWidth: 0.6,
    borderColor: '#000'
  },
  tableRight: {
    width: '25%',
    padding: 5,
    textAlign: 'right'
  },
  grandRow: {
    backgroundColor: '#f2f2f2',
    fontWeight: 'bold'
  },

  /* ================= FOOTER ================= */
  footer: {
    flexDirection: 'row',
    marginTop: 12
  },
  bank: { width: '60%' },
  sign: { width: '40%', textAlign: 'right' },
  signImg: { width: 70, height: 35, marginLeft: 'auto' },

  /* ================= TERMS ================= */
  terms: { marginTop: 10 },
  termRow: { flexDirection: 'row', marginBottom: 3 },
  bullet: { width: 10 },
  termText: { flex: 1 }
});

const QuotationPDFTemplate = ({
  item,
  company,
  bankData,
  signatures,
  terms
}) => {
  if (!item) return null;

  const charges = [
    ['Transportation Charge', item.transportation_charge],
    ['Packing Charge', item.packing_charge],
    ['Unpacking Charge', item.unpacking_charge],
    ['Loading Charge', item.loading_charge],
    ['Unloading Charge', item.unloading_charge],
    ['Insurance Charge', item.insurance_charge],
    ['Storage Charge', item.storage_charge],
    ['Other Charge', item.other_charge],
    ['Service Charge', item.service_charge],
    ['Sub Total', item.total],
    [`SGST (${item.sgst_per}%)`, item.sgst_charge],
    [`CGST (${item.cgst_per}%)`, item.cgst_charge],
    [`IGST (${item.gst_per}%)`, item.gst_charge]
  ];
const parseTerms = (html = '') => {
  if (!html) return [];

  return html
    .replace(/<\/ul>/gi, '')
    .replace(/<ul>/gi, '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/li>/gi, '\n')
    .replace(/<li>/gi, '')
    .replace(/<[^>]*>/g, '')
    .split('\n')
    .map(t => t.trim())
    .filter(Boolean);
};
  return (
    <Document>
      <Page size="A4" style={styles.page}>

        {/* HEADER */}
        <View style={styles.header}>
          {company?.base64URL && <Image src={company.base64URL} style={styles.logo} />}
          <View style={styles.companyRight}>
            <Text style={styles.companyName}>{company.company_name}</Text>
            <Text>{company.company_add}</Text>
            <Text>{company.email} | {company.phone_number}</Text>
            <Text>GSTIN: {company.company_GST}</Text>
          </View>
        </View>

        {/* ORANGE BAR */}
        <View style={styles.orangeBar}>
          <Text style={styles.barText}>Quotation No: {item.quotation_number}</Text>
          <Text style={styles.barText}>Service: {item.service_name}</Text>
          <Text style={styles.barText}>Date: {item.quotation_date}</Text>
        </View>

        {/* CUSTOMER */}
        <View style={styles.row}>
          <Text style={styles.label}>Customer Name:</Text>
          <Text style={styles.value}>{item.name}</Text>
          <Text style={styles.label}>Mobile:</Text>
          <Text style={styles.value}>{item.phone_number}</Text>
        </View>

        <View style={styles.row}>
          <Text style={styles.label}>Start Date:</Text>
          <Text style={styles.value}>{item.shift_start_date}</Text>
          <Text style={styles.label}>End Date:</Text>
          <Text style={styles.value}>{item.shift_end_date}</Text>
          <Text style={styles.label}>GST:</Text>
          <Text style={{ width: '15%' }}>{item.c_gst}</Text>
        </View>

        <View style={styles.row}>
          <Text style={styles.label}>Address:</Text>
          <Text style={{ width: '55%' }}>{item.customer_address}</Text>
          
        </View>

        {/* FROM */}
        <View style={styles.box}>
          <View style={styles.boxHeader}>
            <Text style={styles.boxCol}>From State: {item.from_state}</Text>
            <Text style={styles.boxCol}>City: {item.from_city}</Text>
            <Text style={styles.boxCol}>Floor: {item.select_floor}</Text>
            <Text style={styles.boxCol}>Lift: {item.select_lift}</Text>
          </View>
          <Text style={styles.boxAddress}>{item.from_address}</Text>
        </View>

        {/* TO */}
        <View style={styles.box}>
          <View style={styles.boxHeader}>
            <Text style={styles.boxCol}>To State: {item.to_state}</Text>
            <Text style={styles.boxCol}>City: {item.to_city}</Text>
            <Text style={styles.boxCol}>Floor: {item.select_to_floor}</Text>
            <Text style={styles.boxCol}>Lift: {item.select_to_lift}</Text>
          </View>
          <Text style={styles.boxAddress}>{item.to_address}</Text>
        </View>

        {/* ITEMS */}
        <Text style={{ marginTop: 6, fontWeight: 'bold' }}>Shifting Items:</Text>
        <Text>{item.shift_items}</Text>

        {/* TABLE */}
        <View style={styles.table}>
          <Text style={styles.tableTitle}>Shifting Charges</Text>
          {charges.map((c, i) => (
            <View key={i} style={styles.tableRow}>
              <Text style={styles.tableLeft}>{c[0]}</Text>
              <Text style={styles.tableRight}>{c[1] || 0}</Text>
            </View>
          ))}
          <View style={[styles.tableRow, styles.grandRow]}>
            <Text style={styles.tableLeft}>Grand Total</Text>
            <Text style={styles.tableRight}>₹ {item.total_amount}</Text>
          </View>
        </View>

        {/* FOOTER */}
        <View style={styles.footer}>
          <View style={styles.bank}>
            <Text style={{ fontWeight: 'bold' }}>Bank Details:</Text>
            {bankData?.map((b, i) => (
              <Text key={i}>
                {b.bank_name} | {b.account_number} | IFSC: {b.iffc_code}
              </Text>
            ))}
          </View>
          <View style={styles.sign}>
            <Text style={{ color: 'blue', fontWeight: 'bold' }}>
              For: {company.company_name}
            </Text>
            {signatures?.map((s, i) => (
              <Image key={i} src={s.signature_data} style={styles.signImg} />
            ))}
            <Text>Authorized Signature</Text>
          </View>
        </View>

        {/* TERMS */}
        <View style={styles.terms}>
  <Text style={{ fontWeight: 'bold', marginBottom: 4 }}>
    Terms & Conditions:
  </Text>

  {terms?.map((t, i) =>
    parseTerms(t.tearms).map((line, j) => (
      <View key={`${i}-${j}`} style={styles.termRow}>
        <Text style={styles.bullet}>•</Text>
        <Text style={styles.termText}>{line}</Text>
      </View>
    ))
  )}
</View>


      </Page>
    </Document>
  );
};

export default QuotationPDFTemplate;
